/* eslint-disable react/display-name */
import { yupResolver } from '@hookform/resolvers/yup'
import CloseRounded from '@mui/icons-material/CloseRounded'
import {
  Box,
  Button,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
} from '@mui/material'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'
import { taxisAPI } from '../../../../api/vehicle/taxisAPI'
import { FixedRoutePricingSchema } from '../../../../app/validationSchemas/Supplier/FixedRoutePricingSchema'
import ConfirmDialog from '../../../ConfirmDialog/ConfirmDialog'
import FormInput from '../../../FormFields/FormInput/FormInput'
import Notification from '../../../Notification/Notification'
import ChargeExceptionComponent from './ChargeExceptionComponent'
import FixedRouteSelect from './FixedRouteSelect'
import styles from './fixedRoutesStyle'
import TaxiSelect from './TaxiSelect'

export interface NotificationType {
  isOpen: boolean
  message: string
  type: string
}
const FixedRoutePricing = () => {
  const [openForm, setOpenForm] = React.useState(false)
  const [notify, setNotify] = React.useState<NotificationType>({
    isOpen: false,
    message: '',
    type: '',
  })
  const [confirmDialog, setConfirmDialog] = React.useState({
    isOpen: false,
    title: '',
    subtitle: '',
    onConfirm: '' as any,
  })

  const { mutateAsync: mutate } = useMutation({
    mutationFn: (payload: { fixedRoute: any }) =>
      taxisAPI.createFixedRoute(
        payload.fixedRoute.vehicleId,
        payload.fixedRoute,
      ),
  })

  const formMethods = useForm({
    resolver: yupResolver(FixedRoutePricingSchema),
    defaultValues: { vehicleIds: [] },
  })

  const onSubmit = async (data: any, event: any) => {
    event.preventDefault()
    const { vehicleIds, ...fixedRouteData } = data

    // Handle both single and multiple selections
    const vehicleIdArray = Array.isArray(vehicleIds) ? vehicleIds : [vehicleIds]

    try {
      let successCount = 0
      let errorCount = 0

      // Call API for each selected taxi
      for (const vehicleId of vehicleIdArray) {
        const payload = {
          fixedRoute: {
            ...fixedRouteData,
            vehicleId,
          },
        }

        try {
          const response = await mutate(payload)
          if (response?.status === 201) {
            successCount++
          } else {
            errorCount++
          }
        } catch (error) {
          errorCount++
        }
      }

      if (errorCount === 0) {
        setOpenForm(false)
        formMethods.reset({ vehicleIds: [] })
        setNotify({
          isOpen: true,
          message: `Route assigned to ${successCount} vehicle${
            successCount > 1 ? 's' : ''
          }.`,
          type: 'success',
        })
      } else if (successCount > 0) {
        setNotify({
          isOpen: true,
          message: `Route assigned to ${successCount} vehicle${
            successCount > 1 ? 's' : ''
          }, ${errorCount} failed.`,
          type: 'warning',
        })
      } else {
        setNotify({
          isOpen: true,
          message: 'Error assigning route to vehicles.',
          type: 'error',
        })
      }
    } catch (error) {
      setNotify({
        isOpen: true,
        message: 'Error assigning route to vehicles.',
        type: 'error',
      })
    }
  }

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} sx={styles.buttonGrid}>
          <Button
            variant="outlined"
            className={`${styles.round} ${styles['tumblr']}`}
            onClick={() => {
              setOpenForm(true)
            }}
          >
            Assign fixed route
          </Button>
        </Grid>
      </Grid>
      <Drawer
        anchor={'right'}
        open={openForm}
        onClose={() => {
          setOpenForm(false)
        }}
        PaperProps={{ sx: styles.drawerPaper }}
      >
        <FormProvider {...formMethods}>
          <Box
            component="form"
            onSubmit={formMethods.handleSubmit(onSubmit)}
            sx={styles.drawerForm}
          >
            <Box sx={styles.drawerHeader}>
              <Box>
                <Typography sx={styles.drawerEyebrow}>Fixed Routes</Typography>
                <Typography variant="h5" sx={styles.drawerTitle}>
                  Assign fixed route
                </Typography>
              </Box>
              <IconButton
                aria-label="close assign fixed route panel"
                onClick={() => setOpenForm(false)}
                sx={styles.closeButton}
              >
                <CloseRounded />
              </IconButton>
            </Box>
            <Box sx={styles.drawerBody}>
              <Box sx={styles.formSection}>
                <Box sx={styles.sectionHeader}>
                  <Box>
                    <Typography sx={styles.sectionTitle}>
                      Route &amp; vehicles
                    </Typography>
                    <Typography sx={styles.sectionDescription}>
                      Pick a saved route and choose which taxis it should apply
                      to.
                    </Typography>
                  </Box>
                </Box>
                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                    <FixedRouteSelect />
                  </Grid>
                  <Grid item xs={12}>
                    <TaxiSelect multiple={true} />
                  </Grid>
                </Grid>
              </Box>
              <Box sx={styles.formSection}>
                <Box sx={styles.sectionHeader}>
                  <Box>
                    <Typography sx={styles.sectionTitle}>Pricing</Typography>
                    <Typography sx={styles.sectionDescription}>
                      Set the base fare for this route. Add charge exceptions
                      below if it varies by condition.
                    </Typography>
                  </Box>
                </Box>
                <Grid container spacing={2.5}>
                  <Grid item xs={12}>
                    <FormInput
                      name="price"
                      label="Price"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">£</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box sx={styles.formSection}>
                <Box sx={styles.sectionHeader}>
                  <Box>
                    <Typography sx={styles.sectionTitle}>
                      Charge exceptions
                    </Typography>
                    <Typography sx={styles.sectionDescription}>
                      Optional adjustments applied on top of the base price.
                    </Typography>
                  </Box>
                </Box>
                <Grid container spacing={2.5}>
                  <ChargeExceptionComponent />
                </Grid>
              </Box>
            </Box>
            <Box sx={styles.drawerFooter}>
              <Box sx={styles.footerActionGroup}>
                <Button
                  type="submit"
                  variant="contained"
                  sx={styles.primaryAction}
                >
                  Save
                </Button>
              </Box>
            </Box>
          </Box>
        </FormProvider>
      </Drawer>
      <Notification notify={notify} setNotify={setNotify} />
      <ConfirmDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />
    </>
  )
}

export default FixedRoutePricing
